import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import ControlledInputPhone from 'typescript/components/forms/controlled_input_phone';
import ControlledInputText from 'typescript/components/forms/controlled_input_text';
import AddressLine1 from 'typescript/apps/checkout_v3/components/address_form/address_fields/address_line_1';
import AdministrativeDistrictLevel1Select from 'typescript/apps/checkout_v3/components/address_form/address_fields/administrative_district_level_1_select';
import { CountryCode } from 'typescript/constants/localization';
import { US_STATES } from 'typescript/constants/mappings';
import {
  ADDRESS_FIELDS,
  getAddressPrefix,
  AddressType,
} from 'typescript/schemas/address_forms/address_fields';

interface AddressFieldsUSProps {
  addressType?: AddressType;
  disabled?: boolean;
}

const AddressFieldsUS = ({
  addressType = AddressType.shipping,
  disabled = false,
}: AddressFieldsUSProps) => {
  const {
    control,
    watch,
    formState: { dirtyFields },
  } = useFormContext();
  const { t } = useTranslation();
  const prefix = useMemo(() => getAddressPrefix(addressType), [addressType]);
  const isAddressLine1Dirty =
    watch(`${prefix}${ADDRESS_FIELDS.address_line_1}`)?.length > 0 ||
    dirtyFields[`${prefix}${ADDRESS_FIELDS.address_line_1}`];

  return (
    <>
      {addressType === AddressType.shipping && (
        <>
          <ControlledInputText
            className="shop-grid-item-half"
            control={control}
            name={`${prefix}email`}
            label={t('checkout.delivery.addressLabel.email')}
            disabled={disabled}
          />
          <ControlledInputPhone
            className="shop-grid-item-half"
            control={control}
            name={`${prefix}phone`}
            label={t('checkout.delivery.addressLabel.phoneNumber')}
            countryCode={CountryCode.US}
            disabled={disabled}
          />
        </>
      )}
      <ControlledInputText
        className="shop-grid-item-half"
        control={control}
        name={`${prefix}first_name`}
        label={t('checkout.delivery.addressLabel.firstName')}
        disabled={disabled}
      />
      <ControlledInputText
        className="shop-grid-item-half"
        control={control}
        name={`${prefix}last_name`}
        label={t('checkout.delivery.addressLabel.lastName')}
        disabled={disabled}
      />
      <AddressLine1
        className="shop-grid-item-full"
        addressType={addressType}
        addressSuggestionsEnabled={!isAddressLine1Dirty}
        disabled={disabled}
      />
      <ControlledInputText
        className="shop-grid-item-full"
        control={control}
        name={`${prefix}address_line_2`}
        label={t('checkout.delivery.addressLabel.addressLine2')}
        disabled={disabled}
      />
      <ControlledInputText
        className="shop-grid-item-full"
        control={control}
        name={`${prefix}company`}
        label={t('checkout.delivery.addressLabel.addressLine3')}
        disabled={disabled}
      />
      <ControlledInputText
        className="shop-grid-item-third"
        control={control}
        name={`${prefix}postal_code`}
        label={t('checkout.delivery.addressLabel.postalCode')}
        disabled={disabled}
      />
      <ControlledInputText
        className="shop-grid-item-third"
        control={control}
        name={`${prefix}locality`}
        label={t('checkout.delivery.addressLabel.city')}
        disabled={disabled}
      />
      <AdministrativeDistrictLevel1Select
        className="shop-grid-item-third"
        name={`${prefix}administrative_district_level_1`}
        label={t('checkout.delivery.addressLabel.state')}
        options={US_STATES}
        disabled={disabled}
      />
      {addressType === AddressType.billing && (
        <ControlledInputPhone
          className="shop-grid-item-full"
          control={control}
          name={`${prefix}phone`}
          label={t('checkout.delivery.addressLabel.phoneNumber')}
          countryCode={CountryCode.US}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default AddressFieldsUS;
